import { clsx } from 'clsx'
import { useState } from 'react'

import { GoogleAuthButton } from 'components/auth/GoogleAuthButton'
import { LocalhostEmailPasswordAuth } from 'components/auth/LocalhostEmailPasswordAuth'
import { Button } from 'components/ui/Button'
import { showToast } from 'components/ui/toaster'
import { useAuth } from 'contexts/AuthContext'
import { isEmailPasswordAuthHost } from 'utils/auth-host'
import { webLogger } from 'utils/logger'

type AuthButtonProperties = {
  className?: string
  isDisabled?: boolean
}

export const AuthButton = ({ className = '', isDisabled = false }: AuthButtonProperties) => {
  /* --- Section 1: Hooks --- */
  const { user, loading, signOut } = useAuth()

  /* --- Section 2: Local State --- */
  const [submitting, setSubmitting] = useState(false)

  /* --- Section 3: Derived Values --- */
  const isBusy = loading || submitting || isDisabled
  const useLocalEmailPassword =
    typeof globalThis !== 'undefined' &&
    'location' in globalThis &&
    isEmailPasswordAuthHost(globalThis.location.hostname)

  /* --- Section 4: Event Handlers --- */
  const handleSignOut = async () => {
    try {
      setSubmitting(true)
      await signOut()
    } catch (error) {
      webLogger.error('Sign out error', { error })
      showToast('Sign out failed. Please try again.', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  /* --- Section 5: Early Returns --- */
  if (user) {
    return (
      <Button
        aria-busy={isBusy}
        className={clsx('auth-action', className)}
        disabled={isBusy}
        isLoading={isBusy}
        onClick={handleSignOut}
        variant="secondary"
      >
        Sign Out
      </Button>
    )
  }

  if (useLocalEmailPassword) {
    return <LocalhostEmailPasswordAuth className={className} />
  }

  /* --- Section 6: Render --- */
  return <GoogleAuthButton className={className} />
}
